import { useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Check, Copy, Globe, Send } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { fmtPct } from '@/lib/format';
import type { Socials as SocialLinks } from '@/api/types';
import { Tip } from './primitives';

export function Badge({ children, className, tone = 'neutral' }: { children: ReactNode; className?: string; tone?: 'neutral' | 'accent' | 'buy' | 'sell' | 'warn' | 'local' | 'hot' }) {
  const tones = {
    neutral: 'bg-panel-2 text-fg-3 border-line-2',
    accent: 'bg-accent/10 text-accent border-accent/25',
    buy: 'bg-buy/10 text-buy border-buy/25',
    sell: 'bg-sell/10 text-sell border-sell/25',
    warn: 'bg-warn/10 text-warn border-warn/25',
    local: 'bg-local/10 text-local border-local/25',
    hot: 'bg-sell/15 text-sell border-transparent',
  } as const;
  return <span className={cn('inline-flex items-center gap-1 rounded-sm border px-1.5 py-px text-3xs font-semibold tracking-wide uppercase', tones[tone], className)}>{children}</span>;
}

export const Soon = ({ className }: { className?: string }) => (
  <Badge tone="neutral" className={cn('text-fg-3', className)}>
    Soon
  </Badge>
);

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton', className)} />;
}

export function Progress({ value, className, tone = 'accent', height = 6 }: { value: number; className?: string; tone?: 'accent' | 'buy' | 'grad'; height?: number }) {
  const v = Math.max(0, Math.min(100, value));
  const fill = tone === 'buy' ? 'bg-buy' : tone === 'grad' ? 'bg-gradient-to-r from-accent to-buy' : 'bg-accent';
  return (
    <div className={cn('w-full overflow-hidden rounded-full bg-panel-2', className)} style={{ height }} role="progressbar" aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn('h-full rounded-full transition-[width] duration-500', fill)} style={{ width: `${v}%` }} />
    </div>
  );
}

export function Delta({ value, className, arrow = true, digits = 1 }: { value: number; className?: string; arrow?: boolean; digits?: number }) {
  if (!Number.isFinite(value)) return <span className={cn('text-fg-4', className)}>–</span>;
  const up = value > 0;
  const flat = Math.abs(value) < 0.05;
  return (
    <span className={cn('num inline-flex items-center gap-0.5', flat ? 'text-fg-3' : up ? 'text-buy' : 'text-sell', className)}>
      {arrow && !flat && (up ? <ArrowUp className="size-3" strokeWidth={2.5} /> : <ArrowDown className="size-3" strokeWidth={2.5} />)}
      {fmtPct(value, digits)}
    </span>
  );
}

export function CopyButton({ value, label = 'Copy', className, children }: { value: string; label?: string; className?: string; children?: ReactNode }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1200);
    } catch {
      toast.error('Copy failed: the browser blocked clipboard access.');
    }
  };
  return (
    <Tip content={done ? 'Copied' : label}>
      <button type="button" onClick={copy} className={cn('inline-flex items-center gap-1 rounded-sm text-fg-3 transition-colors hover:text-fg', className)} aria-label={label}>
        {children}
        {done ? <Check className="size-3.5 text-buy" /> : <Copy className="size-3.5" />}
      </button>
    </Tip>
  );
}

/** Our own drawing of an "X" glyph for links to x.com profiles. */
export function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn('size-3.5', className)} fill="currentColor" aria-hidden>
      <path d="M4 3.5h4.2l4.1 5.6 4.8-5.6h2.4l-6.1 7.1 6.6 9.9h-4.2l-4.5-6.3-5.4 6.3H3.5l6.6-7.7L4 3.5Zm3 1.6 9.3 13.8h1.6L8.7 5.1H7Z" />
    </svg>
  );
}

export function Socials({ socials, className, size = 'sm' }: { socials: SocialLinks; className?: string; size?: 'sm' | 'md' }) {
  const items: { href: string; label: string; icon: ReactNode }[] = [];
  const ic = size === 'md' ? 'size-4' : 'size-3.5';
  if (socials.twitter) items.push({ href: socials.twitter, label: 'X', icon: <XIcon className={ic} /> });
  if (socials.website) items.push({ href: socials.website, label: 'Website', icon: <Globe className={ic} /> });
  if (socials.telegram) items.push({ href: socials.telegram, label: 'Telegram', icon: <Send className={ic} /> });
  if (!items.length) return null;
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      {items.map((i) => (
        <a key={i.label} href={i.href} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()} className="text-fg-3 transition-colors hover:text-fg" aria-label={i.label}>
          {i.icon}
        </a>
      ))}
    </span>
  );
}

/** 32-bit FNV-1a, for deterministic colours from an address. */
export function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function CoinAvatar({ src, symbol, seed, size = 36, className, rounded = 'md' }: { src?: string; symbol: string; seed: string; size?: number; className?: string; rounded?: 'md' | 'full' | 'lg' }) {
  const [failed, setFailed] = useState(false);
  const h = hash32(seed);
  const r = rounded === 'full' ? 'rounded-full' : rounded === 'lg' ? 'rounded-lg' : 'rounded-md';
  if (!src || failed) {
    const a = h % 360;
    return (
      <span
        className={cn('inline-flex shrink-0 items-center justify-center font-semibold text-white/90', r, className)}
        style={{ width: size, height: size, fontSize: size * 0.4, background: `linear-gradient(135deg, hsl(${a} 60% 42%), hsl(${(a + 70) % 360} 65% 28%))` }}
        aria-hidden
      >
        {(symbol || '?').slice(0, 1).toUpperCase()}
      </span>
    );
  }
  return <img src={src} alt="" width={size} height={size} loading="lazy" decoding="async" onError={() => setFailed(true)} className={cn('shrink-0 bg-panel-2 object-cover', r, className)} style={{ width: size, height: size }} />;
}

export function WalletAvatar({ address, size = 20, className }: { address: string; size?: number; className?: string }) {
  const h = hash32(address);
  const a = h % 360;
  const b = (h >>> 9) % 360;
  return (
    <span
      className={cn('inline-block shrink-0 rounded-full', className)}
      style={{ width: size, height: size, background: `conic-gradient(from ${h % 90}deg, hsl(${a} 65% 55%), hsl(${b} 60% 40%), hsl(${a} 65% 55%))` }}
      aria-hidden
    />
  );
}

export interface SegOption<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

export function Segmented<T extends string>({ value, onChange, options, className, size = 'sm' }: { value: T; onChange: (v: T) => void; options: SegOption<T>[]; className?: string; size?: 'xs' | 'sm' }) {
  return (
    <div className={cn('inline-flex items-center rounded-md border border-line bg-bg-1 p-0.5', className)} role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-[5px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
            size === 'xs' ? 'h-6 px-2 text-2xs' : 'h-7 px-2.5 text-xs',
            o.value === value ? 'bg-hover text-fg' : 'text-fg-3 hover:text-fg-2',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Empty({ title, children, icon, className }: { title: string; children?: ReactNode; icon?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 px-6 py-10 text-center', className)}>
      {icon && <div className="text-fg-4">{icon}</div>}
      <div className="text-sm font-medium text-fg-2">{title}</div>
      {children && <div className="max-w-sm text-xs text-fg-3">{children}</div>}
    </div>
  );
}

export function Stat({ label, children, className, align = 'left' }: { label: ReactNode; children: ReactNode; className?: string; align?: 'left' | 'right' }) {
  return (
    <div className={cn('flex min-w-0 flex-col', align === 'right' && 'items-end', className)}>
      <span className="text-2xs text-fg-3">{label}</span>
      <span className="num truncate text-sm font-semibold text-fg">{children}</span>
    </div>
  );
}
